/**
 * Picture framing (1.0.0-rc.11): how a clip's picture sits in the sequence frame, and how a sequence of any shape
 * sits in an export's frame. One geometry for the browser preview (CSS object-fit and object-position) and the export
 * (FFmpeg scale, crop and pad), so both put the picture in the same place.
 *
 *  - Fit: the whole picture, scaled to fit inside the box and centred (black or transparent bars), as before rc.11.
 *  - Fill: the picture scaled to cover the box and cropped. The reframe offset (−1…1 on each axis) says where the crop
 *    sits: −1 the left (or top) edge of the picture, 0 the centre, 1 the right (or bottom) edge.
 *
 * A clip whose orientation differs from the sequence's (landscape footage in a 9:16 sequence, phone video in 16:9)
 * gets Fill when it is placed; the same orientation stays Fit (4:3 SD in 16:9 keeps its pillarbox).
 */

/** The fits a layer clip can have (and, with Blur, a storyline clip: VIDEO_FIT_MODES). */
export const FIT_MODES = ['fit', 'fill'] as const;
/**
 * The fits a storyline (V1) clip can have (1.7.0): Fit, Fill, or Blur, the whole picture (as Fit) over a blurred copy
 * of itself that fills the frame (as Fill at the centre). Layers have no Blur.
 */
export const VIDEO_FIT_MODES = ['fit', 'fill', 'blur'] as const;
export type FitMode = (typeof VIDEO_FIT_MODES)[number];
export type LayerFitMode = (typeof FIT_MODES)[number];
export const FIT_LABELS: Record<FitMode, string> = { fit: 'Fit', fill: 'Fill', blur: 'Blur' };

export interface Size { width: number; height: number }
export type Orientation = 'landscape' | 'portrait' | 'square';

export function orientationOf(s: Size): Orientation {
  return s.width > s.height ? 'landscape' : s.width < s.height ? 'portrait' : 'square';
}

/**
 * The fit a clip gets when it is placed: 'fill' when the source is landscape and the sequence portrait or square, or
 * the source portrait and the sequence landscape or square; otherwise undefined (Fit, left out of the document). A
 * square source, or one whose size is unknown, is fitted.
 */
export function defaultFit(source: { width?: number | null; height?: number | null } | null | undefined, sequence: Size): FitMode | undefined {
  if (!source?.width || !source?.height) return undefined;
  const src = orientationOf(source as Size);
  if (src === 'square') return undefined;
  return src !== orientationOf(sequence) ? 'fill' : undefined;
}

/** The reframe offset kept within −1…1 (0 when not a number). */
export const clampReframe = (v: number | undefined): number => (v === undefined || !Number.isFinite(v) ? 0 : Math.max(-1, Math.min(1, v)));

/**
 * Where a picture of `source` size lands in a box: its scaled size and its top-left corner relative to the box (both
 * may be outside the box for Fill, where the box crops it). Fit centres it; Fill places the crop by the reframe offset.
 */
export function fitRect(source: Size, box: Size, mode: FitMode = 'fit', reframeX = 0, reframeY = 0): { x: number; y: number; width: number; height: number } {
  const k = mode === 'fill' ? Math.max(box.width / source.width, box.height / source.height) : Math.min(box.width / source.width, box.height / source.height);
  const width = source.width * k;
  const height = source.height * k;
  const fx = mode === 'fill' ? (1 + clampReframe(reframeX)) / 2 : 0.5;
  const fy = mode === 'fill' ? (1 + clampReframe(reframeY)) / 2 : 0.5;
  return { x: (box.width - width) * fx, y: (box.height - height) * fy, width, height };
}

/**
 * The preview's CSS for a picture in its box: object-fit and object-position. A percentage object-position aligns
 * that fraction of the picture with the same fraction of the box, which is fitRect's (box − picture) × fraction.
 */
export function cssFit(mode: FitMode | undefined, reframeX?: number, reframeY?: number): { objectFit: 'contain' | 'cover'; objectPosition: string } {
  if (mode !== 'fill') return { objectFit: 'contain', objectPosition: '50% 50%' };
  const pct = (v: number | undefined) => `${Number(((1 + clampReframe(v)) * 50).toFixed(3))}%`;
  return { objectFit: 'cover', objectPosition: `${pct(reframeX)} ${pct(reframeY)}` };
}

/**
 * The export's FFmpeg filters that put a (square-pixel) picture into a box of exactly `w`×`h`: Fit scales it inside
 * and pads (with `pad`, a colour such as black or black@0; null leaves the picture its fitted size, for layers),
 * Fill scales it to cover and crops at the reframe offset, as fitRect.
 */
export function ffmpegFit(mode: FitMode | undefined, w: number, h: number, opts: { reframeX?: number; reframeY?: number; pad: string | null; flags?: string; evenFitted?: boolean }): string {
  const flags = opts.flags ?? 'lanczos';
  if (mode === 'fill') {
    const fx = ((1 + clampReframe(opts.reframeX)) / 2).toFixed(6);
    const fy = ((1 + clampReframe(opts.reframeY)) / 2).toFixed(6);
    return `scale=${w}:${h}:force_original_aspect_ratio=increase:flags=${flags},crop=${w}:${h}:'(iw-ow)*${fx}':'(ih-oh)*${fy}'`;
  }
  const scale = `scale=${w}:${h}:force_original_aspect_ratio=decrease${opts.evenFitted ? ':force_divisible_by=2' : ''}:flags=${flags}`;
  return opts.pad ? `${scale},pad=${w}:${h}:(ow-iw)/2:(oh-ih)/2:color=${opts.pad}` : scale;
}

/** Two sizes have the same shape (within half a percent, which absorbs rounding to even sizes). */
export const sameAspect = (a: Size, b: Size): boolean => Math.abs(a.width / a.height - b.width / b.height) / (b.width / b.height) < 0.005;

/** The largest even-sized box of `shape`'s aspect inside `frame`, centred (the sequence inside a fixed export frame). */
export function fitInside(shape: Size, frame: Size): Size {
  if (sameAspect(shape, frame)) return { width: frame.width, height: frame.height };
  const k = Math.min(frame.width / shape.width, frame.height / shape.height);
  return { width: Math.max(2, Math.round((shape.width * k) / 2) * 2), height: Math.max(2, Math.round((shape.height * k) / 2) * 2) };
}

/** "16:9", "9:16", "4:5", "1:1"… for a size (reduced; non-standard shapes as a decimal ratio). */
export function aspectLabel(s: Size): string {
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  const g = gcd(s.width, s.height);
  const w = s.width / g;
  const h = s.height / g;
  if (w <= 32 && h <= 32) return `${w}:${h}`;
  return s.width >= s.height ? `${(s.width / s.height).toFixed(2)}:1` : `1:${(s.height / s.width).toFixed(2)}`;
}

/**
 * ffprobe's field order as the field shown first (1.0.0-rc.11): tt and tb are top field first, bb and bt bottom field
 * first (the first letter, as QC and export read it; FFmpeg writes top-field-first ProRes in MOV as tb). Null otherwise.
 */
export function probeFieldOrder(f: string | null | undefined): 'tff' | 'bff' | null {
  if (f === 'tt' || f === 'tb' || f === 'tff') return 'tff';
  if (f === 'bb' || f === 'bt' || f === 'bff') return 'bff';
  return null;
}

/**
 * A video's format as the Library shows it (1.0.0-rc.11): "1080p · 25 fps", "1080i · 25 fps" for interlaced (with a
 * tooltip naming the field order and the field rate), and the size for portrait or square video ("1080×1920 · 25 fps"),
 * where a line count means nothing. An unknown scan reads as progressive, as before.
 */
export function videoFormatLabel(v: {
  width: number | null; height: number | null; frameRate: { num: number; den: number } | null;
  scanType?: 'progressive' | 'interlaced' | null; fieldOrder?: 'tff' | 'bff' | null;
}): { label: string; title: string | null } {
  if (!v.height) return { label: '—', title: null };
  const fpsValue = v.frameRate ? v.frameRate.num / v.frameRate.den : null;
  const fpsText = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, ''));
  const rate = fpsValue ? `${fpsText(fpsValue)} fps` : '—';
  const interlaced = v.scanType === 'interlaced';
  const size = v.width && v.width <= v.height ? `${v.width}×${v.height}` : `${v.height}${interlaced ? 'i' : 'p'}`;
  const first = v.fieldOrder === 'bff' ? 'bottom' : 'top';
  const title = interlaced
    ? `Interlaced, ${first} field first${fpsValue ? `: ${fpsText(fpsValue)} frames (${fpsText(fpsValue * 2)} fields) per second` : ''}`
    : null;
  return { label: `${size}${interlaced && v.width && v.width <= v.height ? ' interlaced' : ''} · ${rate}`, title };
}

/**
 * A graphic in the sequence frame (rc.11): it lays out at `layout` and is scaled by `scale`, its top-left corner at
 * (left, top) in frame pixels. A graphic authored at the frame's shape covers the frame (scaled by the frame's height,
 * as before rc.11); one authored at another shape is fitted inside and centred (`fitted`).
 */
export function graphicPlacement(layout: Size, frame: Size): { layout: Size; scale: number; left: number; top: number; width: number; height: number; fitted: boolean } {
  if (sameAspect(layout, frame)) {
    const scale = frame.height / layout.height;
    return { layout, scale, left: 0, top: 0, width: frame.width, height: frame.height, fitted: false };
  }
  const scale = Math.min(frame.width / layout.width, frame.height / layout.height);
  const width = layout.width * scale;
  const height = layout.height * scale;
  return { layout, scale, left: (frame.width - width) / 2, top: (frame.height - height) / 2, width, height, fitted: true };
}

/* ---------------------------------------------------------------- 1.7.0: zoom, keyframes and the crop window */

/** How the segment that leaves a key moves to the next key (1.7.0). */
export const KEY_EASES = ['smooth', 'linear', 'hold'] as const;
export type KeyEase = (typeof KEY_EASES)[number];
export const KEY_EASE_LABELS: Record<KeyEase, string> = { smooth: 'Smooth', linear: 'Linear', hold: 'Hold' };
/** Zoom of a Fill crop: 1 is the largest crop of the frame's shape, 8 an eighth of it on each side. */
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 8;
/**
 * Blur fit (1.7.0): the Gaussian standard deviation of the blurred, filled background, as a fraction of the frame's
 * short side (0.03 × 1080 ≈ 32 px). Compose's gblur and the preview's CSS / canvas blur() (which takes the same
 * standard deviation, in the displayed picture's pixels) both use it.
 */
export const BLUR_FIT_SIGMA = 0.03;
/** Keys per clip, per format (framing keys) or per motion list. */
export const MAX_KEYS = 64;
/** Parameters interpolated on a log scale (a zoom from 1 to 4 passes 2 halfway, at a steady speed). */
export const LOG_PARAMS: readonly string[] = ['zoom'];

export const clampZoom = (v: number | undefined): number => (v === undefined || !Number.isFinite(v) ? 1 : Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, v)));

/** Anything with a time and an ease, and numeric parameters (framing keys: x, y, zoom; motion keys: x, y, scale, opacity). */
export interface CurveKeyBase { atTicks: number; ease: KeyEase }

/**
 * One piece of a curve: between t0 and t1 (ticks) the value is a + b·u + c·u² + d·u³ with u = (t − t0) / (t1 − t0),
 * in the interpolation space (ln for LOG_PARAMS). Shared by the preview (valueAt) and the export (ffmpegCurve).
 */
export interface CurveSegment { t0: number; t1: number; a: number; b: number; c: number; d: number }

/** A parameter's curve: its segments, and the (interpolation-space) values held before the first and after the last key. */
export interface Curve { segments: CurveSegment[]; before: number; after: number; log: boolean; t0: number; t1: number }

function paramPoints<K extends CurveKeyBase>(keys: readonly K[], param: string, ticksPerFrame?: number): Array<{ t: number; v: number; ease: KeyEase }> {
  const log = LOG_PARAMS.includes(param);
  const pts: Array<{ t: number; v: number; ease: KeyEase }> = [];
  for (const k of keys) {
    const raw = (k as unknown as Record<string, unknown>)[param];
    if (typeof raw !== 'number' || !Number.isFinite(raw)) continue;
    const v = log ? Math.log(Math.max(1e-6, raw)) : raw;
    const last = pts.at(-1);
    // Keys closer than a frame (or at the same time) are one key: the later one wins.
    if (last && (k.atTicks <= last.t || (ticksPerFrame && k.atTicks - last.t < ticksPerFrame))) { pts[pts.length - 1] = { t: Math.max(last.t, k.atTicks), v, ease: k.ease }; continue; }
    pts.push({ t: k.atTicks, v, ease: k.ease });
  }
  return pts;
}

/**
 * The curve of one parameter through the keys that set it (keys sorted by atTicks; a key without the parameter is
 * skipped). The ease belongs to the segment that leaves a key: Hold keeps the key's value up to the next key, Linear
 * goes straight, Smooth is a monotone cubic Hermite (Fritsch–Carlson; tangents by the weighted harmonic mean, 0 at
 * the ends and next to a Hold, the straight segment's slope next to a Linear one, clamped so it never overshoots).
 * Zoom is interpolated as ln(zoom). `ticksPerFrame`: keys closer than one frame merge (the later one wins).
 */
export function curveOf<K extends CurveKeyBase>(keys: readonly K[], param: string, ticksPerFrame?: number): Curve | null {
  const pts = paramPoints(keys, param, ticksPerFrame);
  if (!pts.length) return null;
  const log = LOG_PARAMS.includes(param);
  const n = pts.length;
  const h = (k: number) => pts[k + 1]!.t - pts[k]!.t;
  const delta = (k: number) => (pts[k + 1]!.v - pts[k]!.v) / h(k);
  const ease = (k: number) => pts[k]!.ease; // of the segment k → k+1
  // Tangents (value per tick) at each key, as seen from a smooth segment.
  const m = new Array<number>(n).fill(0);
  for (let k = 0; k < n; k++) {
    const leftSmooth = k > 0 && ease(k - 1) === 'smooth';
    const rightSmooth = k < n - 1 && ease(k) === 'smooth';
    if (!leftSmooth && !rightSmooth) continue;
    if (leftSmooth && rightSmooth) {
      const d0 = delta(k - 1), d1 = delta(k);
      if (d0 * d1 <= 0) { m[k] = 0; continue; }
      const h0 = h(k - 1), h1 = h(k);
      m[k] = (3 * (h0 + h1)) / ((2 * h1 + h0) / d0 + (h1 + 2 * h0) / d1);
      continue;
    }
    // One smooth side: continue the other side's slope when it is linear and goes the same way, else start from rest.
    const own = leftSmooth ? delta(k - 1) : delta(k);
    const otherIdx = leftSmooth ? k : k - 1;
    const other = otherIdx >= 0 && otherIdx < n - 1 && ease(otherIdx) === 'linear' ? delta(otherIdx) : 0;
    m[k] = other * own > 0 ? Math.sign(own) * Math.min(Math.abs(other), 3 * Math.abs(own)) : 0;
  }
  const segments: CurveSegment[] = [];
  for (let k = 0; k < n - 1; k++) {
    const p0 = pts[k]!.v, p1 = pts[k + 1]!.v, t0 = pts[k]!.t, t1 = pts[k + 1]!.t, hk = t1 - t0;
    const e = ease(k);
    if (e === 'hold') { segments.push({ t0, t1, a: p0, b: 0, c: 0, d: 0 }); continue; }
    if (e === 'linear' || p0 === p1) { segments.push({ t0, t1, a: p0, b: p1 - p0, c: 0, d: 0 }); continue; }
    let m0 = m[k]!, m1 = m[k + 1]!;
    const dk = delta(k);
    // Fritsch–Carlson: α² + β² ≤ 9 keeps the segment monotone.
    const al = m0 / dk, be = m1 / dk;
    if (al < 0) m0 = 0;
    if (be < 0) m1 = 0;
    const s = (Math.max(0, al)) ** 2 + (Math.max(0, be)) ** 2;
    if (s > 9) { const tau = 3 / Math.sqrt(s); m0 = tau * Math.max(0, al) * dk; m1 = tau * Math.max(0, be) * dk; }
    const b = hk * m0;
    const c = 3 * (p1 - p0) - 2 * hk * m0 - hk * m1;
    const d = 2 * (p0 - p1) + hk * m0 + hk * m1;
    segments.push({ t0, t1, a: p0, b, c, d });
  }
  return { segments, before: pts[0]!.v, after: pts[n - 1]!.v, log, t0: pts[0]!.t, t1: pts[n - 1]!.t };
}

/** The segments of a parameter's curve (see curveOf); empty with fewer than two keys setting it. */
export function curveSegments<K extends CurveKeyBase>(keys: readonly K[], param: string, ticksPerFrame?: number): CurveSegment[] {
  return curveOf(keys, param, ticksPerFrame)?.segments ?? [];
}

const evalSegment = (s: CurveSegment, t: number): number => {
  const u = s.t1 > s.t0 ? Math.max(0, Math.min(1, (t - s.t0) / (s.t1 - s.t0))) : 0;
  return s.a + u * (s.b + u * (s.c + u * s.d));
};

/** A parameter's value at `ticks` (the keys' time base): held before the first key and after the last; undefined when no key sets it. */
export function valueAt<K extends CurveKeyBase>(keys: readonly K[] | undefined, param: string, ticks: number, ticksPerFrame?: number): number | undefined {
  if (!keys?.length) return undefined;
  const c = curveOf(keys, param, ticksPerFrame);
  if (!c) return undefined;
  let v: number;
  if (ticks <= c.t0) v = c.before;
  else if (ticks >= c.t1) v = c.after;
  else {
    let lo = 0, hi = c.segments.length - 1;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (c.segments[mid]!.t0 <= ticks) lo = mid; else hi = mid - 1; }
    v = evalSegment(c.segments[lo]!, ticks);
  }
  return c.log ? Math.exp(v) : v;
}

/** A number for an FFmpeg expression: fixed notation with six decimals, never an exponent. */
export const ffNum = (v: number): string => (Math.abs(v) < 5e-7 ? 0 : v).toFixed(6);

/**
 * The FFmpeg expression of a parameter's curve in a frame index variable (`in` by default): a balanced tree of
 * if(lt(in,F),L,R) whose leaves are the segments, each `st(1,(in-Fk)/Dk);a+ld(1)*(b+ld(1)*(c+ld(1)*d))` (a Hold, and the
 * values before the first and after the last key, a constant); wrapped in exp() for LOG_PARAMS. `frameOf` turns key
 * ticks into the frame index (fractional is fine; it must be affine in ticks, so u is the same in frames as in ticks).
 * `window`: only the keys inside [f0, f1] (frames) plus one neighbour on each side are written; the tangents still
 * come from every key, so the values inside the window are the same. Null when no key sets the parameter.
 */
export function ffmpegCurve<K extends CurveKeyBase>(keys: readonly K[], param: string, frameOf: (ticks: number) => number, opts: { window?: [number, number]; variable?: string; ticksPerFrame?: number } = {}): string | null {
  const c = curveOf(keys, param, opts.ticksPerFrame);
  if (!c) return null;
  const X = opts.variable ?? 'in';
  let segs = c.segments;
  let before = c.before;
  let after = c.after;
  if (opts.window && segs.length) {
    const [f0, f1] = opts.window;
    // Keys are segment starts plus the last segment's end. Keep keys inside the window and one neighbour each side.
    const times = [...segs.map((s) => s.t0), segs.at(-1)!.t1];
    const frames = times.map(frameOf);
    let first = frames.findIndex((f) => f >= f0);
    if (first < 0) first = frames.length - 1;
    let last = -1;
    for (let i = frames.length - 1; i >= 0; i--) if (frames[i]! <= f1) { last = i; break; }
    if (last < 0) last = 0;
    const lo = Math.max(0, Math.min(first, last) - 1);
    const hi = Math.min(frames.length - 1, Math.max(first, last) + 1);
    // Keys lo..hi → segments lo..hi-1.
    if (lo > 0) before = segs[lo]!.a;
    if (hi < frames.length - 1) after = segs[hi]!.a;
    segs = segs.slice(lo, hi);
  }
  type Leaf = { expr: string };
  const leaves: Leaf[] = [{ expr: ffNum(before) }];
  const bounds: number[] = []; // bounds[i]: where leaf i+1 starts
  if (segs.length) {
    bounds.push(frameOf(segs[0]!.t0));
    for (const s of segs) {
      const F0 = frameOf(s.t0), F1 = frameOf(s.t1);
      const D = F1 - F0;
      const constant = s.b === 0 && s.c === 0 && s.d === 0;
      leaves.push({ expr: constant || D <= 0 ? ffNum(s.a) : `st(1,(${X}-${ffNum(F0)})/${ffNum(D)});${ffNum(s.a)}+ld(1)*(${ffNum(s.b)}+ld(1)*(${ffNum(s.c)}+ld(1)*${ffNum(s.d)}))` });
      bounds.push(F1);
    }
    leaves.push({ expr: ffNum(after) });
  }
  const build = (lo: number, hi: number): string => {
    if (lo === hi) return leaves[lo]!.expr;
    const mid = (lo + hi + 1) >> 1;
    return `if(lt(${X},${ffNum(bounds[mid - 1]!)}),${build(lo, mid - 1)},${build(mid, hi)})`;
  };
  const tree = build(0, leaves.length - 1);
  return c.log ? `exp(${tree})` : tree;
}

/** The framing of a picture clip for one format, as cropAt reads it. */
export interface FramingLike {
  fit?: FitMode;
  reframeX?: number;
  reframeY?: number;
  zoom?: number;
  keys?: ReadonlyArray<CurveKeyBase & { x: number; y: number; zoom: number }>;
}

/** A crop window as fractions of the source picture: its top-left corner and size. */
export interface CropWindow { x: number; y: number; w: number; h: number }

/** The Fill framing's pan (−1…1) and zoom at a source time: from the keys when there are any, else the static values. */
export function framingAt(framing: FramingLike, sourceTicks: number): { x: number; y: number; zoom: number } {
  if (framing.keys?.length) {
    return {
      x: clampReframe(valueAt(framing.keys, 'x', sourceTicks)),
      y: clampReframe(valueAt(framing.keys, 'y', sourceTicks)),
      zoom: clampZoom(valueAt(framing.keys, 'zoom', sourceTicks)),
    };
  }
  return { x: clampReframe(framing.reframeX), y: clampReframe(framing.reframeY), zoom: clampZoom(framing.zoom) };
}

/**
 * The part of the source picture a Fill clip shows at `sourceTicks` (1.7.0), as fractions of the source: the largest
 * window of the frame's shape (w0 = min(1, Af/As), h0 = min(1, As/Af)), divided by the zoom, placed by the pan in the
 * spare picture around it (x = (1 − w)(1 + px)/2). Null for Fit and Blur (the whole picture is shown). At zoom 1 this
 * is exactly fitRect's Fill crop.
 */
export function cropAt(sourceAspect: number, frameAspect: number, framing: FramingLike, sourceTicks: number): CropWindow | null {
  if (framing.fit !== 'fill') return null;
  const { x: px, y: py, zoom } = framingAt(framing, sourceTicks);
  const w0 = Math.min(1, frameAspect / sourceAspect);
  const h0 = Math.min(1, sourceAspect / frameAspect);
  const w = w0 / zoom;
  const h = h0 / zoom;
  return { x: ((1 - w) * (1 + px)) / 2, y: ((1 - h) * (1 + py)) / 2, w, h };
}

/**
 * How much a clip's picture is enlarged in a frame (frame pixels per source pixel), for the inspector's badge: Fit and
 * Blur by the fitted size; Fill by its crop at the largest zoom it reaches (the static zoom or its keys). 1.5 and more
 * is worth a warning ("×1.8").
 */
export function upscaleFactor(source: Size, frame: Size, framing: FramingLike): number {
  if (!source.width || !source.height) return 1;
  if (framing.fit !== 'fill') return Math.min(frame.width / source.width, frame.height / source.height);
  const zooms = framing.keys?.length ? framing.keys.map((k) => clampZoom(k.zoom)) : [clampZoom(framing.zoom)];
  const zoom = Math.max(...zooms);
  const w0 = Math.min(1, frame.width / frame.height / (source.width / source.height));
  return frame.width / ((w0 / zoom) * source.width);
}
